import type { PatientResponse } from '@emr/shared';
import { useQuery } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { searchPatients } from '../../api/patients.ts';
import { GatedRoute } from '../../components/GatedRoute.tsx';
import { DataTable, type DataTableColumn } from '../../components/DataTable.tsx';
import { Button, Field, Panel, SelectField } from '../../components/primitives.tsx';
import { LoadingNotice } from '../../components/AsyncState.tsx';
import { formatCalendarDate } from '../../lib/dateFormat.ts';
import { QueryFailureNotice } from './QueryFailureNotice.tsx';

/**
 * The patient list: search by one field at a time, open a chart.
 *
 * `patient:read` only — this is the administrative/demographics surface `patientsRoutes.ts`'s own
 * header comment carves out (front desk and billing need to find and register patients with no
 * treatment relationship at all), not `chart:read`. A role that can search patients but not read
 * charts still gets a working list here; the chart tabs behind `/patients/:patientId` are what
 * additionally require `chart:read`.
 *
 * **One "search by" filter, not several simultaneous boxes** — direct user instruction, 2026-08-18:
 * a "search by" `SelectField` picks which of last name / first name / date of birth / MRN the one
 * search input matches against, MRN listed last since it's the rarest lookup in practice (a front
 * desk or clinician asks a patient's name or birthdate far more often than reads off a chart
 * number). The input itself changes type with the selection — a native `type="date"` for date of
 * birth (its value format, `YYYY-MM-DD`, already matches `calendarDateSchema` exactly, so no
 * conversion helper is needed the way `AppointmentFormDialog.tsx`'s `datetime-local` field needs
 * one), a plain text field otherwise.
 */

type SearchField = 'lastName' | 'firstName' | 'dateOfBirth' | 'mrn';

/** Built from `t` rather than a module-level constant — `useTranslation`'s `t` is only available
 * inside a component, and these need to re-derive whenever the active language changes. */
function searchFieldOptions(
  t: TFunction<'patients'>,
): readonly { value: SearchField; label: string }[] {
  return [
    { value: 'lastName', label: t('fields.lastName') },
    { value: 'firstName', label: t('fields.firstName') },
    { value: 'dateOfBirth', label: t('fields.dateOfBirth') },
    // Listed last, deliberately — MRN search is rare in practice; the default and the common cases
    // sit above it.
    { value: 'mrn', label: t('fields.mrn') },
  ];
}

function columns(t: TFunction<'patients'>): readonly DataTableColumn<PatientResponse>[] {
  return [
    {
      key: 'mrn',
      header: t('table.columns.mrn'),
      cell: (patient) => patient.mrn,
      wrap: 'anywhere',
      maxWidthCh: 14,
      cellClassName: 'font-mono',
    },
    {
      key: 'name',
      header: t('table.columns.name'),
      cell: (patient) => `${patient.lastName}, ${patient.firstName}`,
      // 'wrap', not 'anywhere': a name has a natural space at the comma, but the last name itself is
      // still unbounded (`MAX_NAME_LENGTH` — 200 chars) — a real-browser check with a genuinely
      // unbroken 80-character last name (`DataTable.tsx`'s own header comment) is what caught this
      // needing a `maxWidthCh` at all, not just the MRN column.
      maxWidthCh: 20,
    },
    {
      key: 'dob',
      header: t('table.columns.dob'),
      // Every displayed date goes through `dateFormat.ts` — never the raw ISO string ad hoc per
      // component (`dateFormat.ts`'s own header comment); `PatientChartPage.tsx` already formats this
      // identical field correctly for the same patient.
      cell: (patient) => formatCalendarDate(patient.dateOfBirth),
      wrap: 'nowrap',
    },
    {
      key: 'sex',
      header: t('table.columns.sex'),
      cell: (patient) =>
        patient.genderIdentity === null
          ? patient.administrativeSex
          : `${patient.administrativeSex} / ${patient.genderIdentity}`,
      wrap: 'anywhere',
      maxWidthCh: 16,
    },
    {
      key: 'contact',
      header: t('table.columns.contact'),
      cell: (patient) => {
        if (patient.phone === null && patient.email === null) return '—';
        return [patient.phone, patient.email].filter((value) => value !== null).join(' · ');
      },
      wrap: 'anywhere',
      maxWidthCh: 20,
    },
    {
      key: 'open',
      header: t('table.columns.chart'),
      cell: (patient) => (
        <Link
          to={`/patients/${patient.id}`}
          className="font-medium text-accent-text underline-offset-2 hover:underline"
        >
          {t('table.openChart')}
        </Link>
      ),
      wrap: 'nowrap',
      cardHidesLabel: true,
    },
  ];
}

interface SearchFilters {
  readonly mrn: string | undefined;
  readonly name: string | undefined;
  readonly firstName: string | undefined;
  readonly dateOfBirth: string | undefined;
}

const EMPTY_FILTERS: SearchFilters = {
  mrn: undefined,
  name: undefined,
  firstName: undefined,
  dateOfBirth: undefined,
};

function hasActiveFilter(filters: SearchFilters): boolean {
  return (
    filters.mrn !== undefined ||
    filters.name !== undefined ||
    filters.firstName !== undefined ||
    filters.dateOfBirth !== undefined
  );
}

export function PatientListPage() {
  const { t } = useTranslation('patients');
  return (
    <GatedRoute title={t('pageTitle')} capability="patient:read" feature={null}>
      <PatientSearch />
    </GatedRoute>
  );
}

function PatientSearch() {
  const { t } = useTranslation('patients');
  const { t: tDuplicates } = useTranslation('patientDuplicates');
  const [searchField, setSearchField] = useState<SearchField>('lastName');
  const [inputValue, setInputValue] = useState('');
  const [filters, setFilters] = useState<SearchFilters>(EMPTY_FILTERS);

  const patients = useQuery({
    queryKey: ['patients', 'search', filters],
    queryFn: ({ signal }) => searchPatients(filters, signal),
  });

  const active = hasActiveFilter(filters);
  const fieldOptions = useMemo(() => searchFieldOptions(t), [t]);
  const tableColumns = useMemo(() => columns(t), [t]);

  return (
    <div className="flex flex-col gap-4">
      {/* Duplicate-match review queue entry point (BACKLOG.md C2a) — reachable from here rather than
          orphaned; this page is where staff already are while registering/searching patients, the
          same moment a likely-duplicate flag is most useful. */}
      <Link
        to="/patients/duplicates"
        className="self-start text-sm font-medium text-accent-text underline-offset-2 hover:underline"
      >
        {tDuplicates('entryPointLink')}
      </Link>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          const value = inputValue.trim();
          setFilters({
            mrn: searchField === 'mrn' && value !== '' ? value : undefined,
            name: searchField === 'lastName' && value !== '' ? value : undefined,
            firstName: searchField === 'firstName' && value !== '' ? value : undefined,
            dateOfBirth: searchField === 'dateOfBirth' && value !== '' ? value : undefined,
          });
        }}
        className="flex flex-col gap-3 sm:flex-row sm:items-end sm:gap-3"
      >
        <div className="w-full sm:w-48">
          <SelectField
            label={t('searchByLabel')}
            value={searchField}
            onChange={(event) => {
              setSearchField(event.target.value as SearchField);
              setInputValue('');
            }}
          >
            {fieldOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </SelectField>
        </div>
        <div className="flex-1">
          {searchField === 'dateOfBirth' ? (
            <Field
              label={t('fields.dateOfBirth')}
              type="date"
              value={inputValue}
              onChange={(event) => {
                setInputValue(event.target.value);
              }}
            />
          ) : (
            <Field
              label={fieldOptions.find((option) => option.value === searchField)?.label ?? ''}
              value={inputValue}
              onChange={(event) => {
                setInputValue(event.target.value);
              }}
              placeholder={
                searchField === 'mrn'
                  ? t('placeholderMrn')
                  : searchField === 'firstName'
                    ? t('placeholderMatchedFirst')
                    : t('placeholderMatchedLast')
              }
            />
          )}
        </div>
        <Button type="submit" pending={patients.isFetching && !patients.isPending}>
          {t('submit')}
        </Button>
        {active ? (
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              setInputValue('');
              setFilters(EMPTY_FILTERS);
            }}
          >
            {t('clear')}
          </Button>
        ) : null}
      </form>

      <Panel>
        {patients.isPending ? (
          <LoadingNotice label={t('loading')} />
        ) : patients.isError ? (
          <QueryFailureNotice error={patients.error} onRetry={() => void patients.refetch()} />
        ) : (
          <DataTable
            caption={t('table.caption')}
            columns={tableColumns}
            rows={patients.data}
            rowKey={(patient) => patient.id}
            minWidthRem={42}
            emptyMessage={active ? t('table.emptyFiltered') : t('table.emptyDefault')}
          />
        )}
      </Panel>
    </div>
  );
}
